// src/utils/pushNotifications.js
import { PushNotifications } from '@capacitor/push-notifications';
import { Capacitor } from '@capacitor/core';

// The APNs token for THIS device. Needed at logout so the server can
// unsubscribe only this device - an unregister without a token turns off
// every device of the account (e.g. logging out on Device X would silence
// Device Y too). Persisted so it survives app restarts: the 'registration'
// event only fires when register() is called, not on cold start.
const TOKEN_KEY = 'push_device_token';
let deviceToken = null;
try { deviceToken = localStorage.getItem(TOKEN_KEY); } catch { /* ignore */ }

// Listener handles, so repeated registerForPush() calls (every login and
// every app launch) replace the listeners instead of stacking duplicates.
let listeners = [];

async function removeListeners() {
  await Promise.all(listeners.map((l) => l.remove().catch(() => {})));
  listeners = [];
}

// userType: 'admin' | 'client' - decides where a notification tap lands.
export async function registerForPush(userType) {
  if (!Capacitor.isNativePlatform()) return;

  const perm = await PushNotifications.requestPermissions();
  if (perm.receive !== 'granted') return;

  await removeListeners();

  // Listeners must be attached BEFORE calling register() - if the native
  // 'registration' event fires quickly, a listener attached afterward can
  // miss it entirely.
  listeners.push(await PushNotifications.addListener('registration', async (token) => {
    deviceToken = token.value;
    try { localStorage.setItem(TOKEN_KEY, token.value); } catch { /* ignore */ }

    try {
      // Registering is also how this device "claims" the token: the server
      // deactivates it for any other account that still holds it.
      const res = await fetch('/api/mobile/register-push-token', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          expo_push_token: token.value, // legacy field name, holds the APNs token
          device_info: { device_os: Capacitor.getPlatform() },
        }),
      });
      if (!res.ok) {
        console.error('register-push-token failed:', res.status, await res.text());
      }
    } catch (err) {
      console.error('register-push-token request failed:', err);
    }
  }));

  listeners.push(await PushNotifications.addListener('registrationError', (err) => {
    console.error('Push registration failed:', err);
  }));

  // Tapping a notification opens the page it is about. Support ticket pushes
  // carry data.route (e.g. "/support?ticket=12"); everything else opens the
  // alerts page for the logged-in account. (Replaces the old React Native
  // payload `navigation.screen`, which nothing in Capacitor reads.) Capacitor
  // keeps a cold-start tap until a listener is attached, so taps that launch
  // the app are handled too.
  listeners.push(await PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
    const route = action?.notification?.data?.route;
    const target = typeof route === 'string' && route.startsWith('/') && !route.startsWith('//')
      ? route
      : (userType === 'client' ? '/alerts' : '/admin/alerts');
    if (window.location.pathname + window.location.search !== target) {
      window.location.href = target;
    }
  }));

  await PushNotifications.register(); // called last
}

// Call at logout, BEFORE /api/auth/logout clears the auth cookies.
// Unsubscribes only this device; other devices on the same account keep
// receiving alerts. Never throws - logout must continue regardless.
export async function unregisterFromPush() {
  if (!Capacitor.isNativePlatform()) return;

  await removeListeners();

  if (!deviceToken) return; // never registered here, e.g. permission denied

  try {
    const res = await fetch('/api/mobile/unregister-push-token', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ expo_push_token: deviceToken }),
    });
    if (!res.ok) {
      console.error('unregister-push-token failed:', res.status);
    }
  } catch (err) {
    // Not fatal: the next account to log in on this device claims the token
    // on the server anyway.
    console.error('unregister-push-token request failed:', err);
  }
}
